import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  UIButton,
  UIPageFormFooter,
  UIPageHeader,
  UIPhoneField,
  UISelect,
  UITextField,
  UIUpload,
} from "../../../../components/ui";
import { uploadFileWithProgress } from "../../../../utils/Helpers";
import { getCompanyRolesRequest } from "../../../../store/CompanyPortal/Roles/actions";
import {
  clearCompanyUser,
  deleteCompanyUserRequest,
  getCompanyUserRequest,
  updateCompanyUserRequest,
} from "../../../../store/CompanyPortal/Employees/actions";
import {
  buildFullInternationalPhone,
  buildUpdateEmployeePayload,
  createEmployeeEditBaseline,
  isEmployeeEditDirty,
  resolveCompanyRoleOption,
} from "../employeeMappers";
import DeleteEmployeeModal from "../DeleteEmployeeModal";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  canDeleteCompanyModule,
  canEditCompanyModule,
  canViewCompanyModules,
} from "../../../../utils/Permissions";

import "../AddEmployee/AddEmployee.scss";
import "./ViewEmployee.scss";

const EMPLOYEES_TAB_PATH = `${ROUTE_PATHS.companyTeamsPermissions}?tab=employees`;

/** A single read-only label/value pair in the Details grid (view mode). */
const DetailField = ({ label, value, emptyValue, ltr }) => (
  <div className="view-employee__field">
    <span className="view-employee__field-label">{label}</span>
    <span className="view-employee__field-value" dir={ltr ? "ltr" : undefined}>
      {value || emptyValue}
    </span>
  </div>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  emptyValue: PropTypes.string,
  ltr: PropTypes.bool,
};

DetailField.defaultProps = {
  value: "",
  emptyValue: "—",
  ltr: false,
};

/** Resolve the employee's role to a dropdown option object. */
const resolveRoleOption = resolveCompanyRoleOption;

const ViewEmployee = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const employee = useSelector((state) => state.companyEmployees.current);
  const employeesModule = companyPermissionModules.employees;
  const canEdit = canEditCompanyModule(employeesModule, login);
  const canDelete = canDeleteCompanyModule(employeesModule, login);
  const canViewRoles = canViewCompanyModules(
    companyPermissionModules.rolesManagement,
    login,
  );
  const deleting = useSelector((state) => state.companyEmployees.deleting);
  const updating = useSelector((state) => state.companyEmployees.updating);
  const roleOptions = useSelector((state) => state.companyRoles.list);
  const teamCopy = messages[lang].teamManagement;
  const copy = teamCopy.viewEmployee;
  // Form-field wording (labels, placeholders, validations, upload) is shared
  // with the Add Employee screen.
  const formCopy = teamCopy.addEmployee;

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  // Editable form state (only meaningful while `editing`).
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryDialCode, setPhoneCountryDialCode] = useState("966");
  const [phoneCountryIso, setPhoneCountryIso] = useState("sa");
  const [role, setRole] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [uploadState, setUploadState] = useState({ target: null, percent: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});
  const editBaselineRef = useRef(null);
  const editFromListRef = useRef(Boolean(location.state?.startInEdit));

  const goBackToList = useCallback(
    () => history.push(EMPLOYEES_TAB_PATH),
    [history],
  );

  useEffect(() => {
    if (!id) return undefined;

    dispatch(
      getCompanyUserRequest({
        id,
        onError: () => goBackToList(),
      }),
    );

    return () => dispatch(clearCompanyUser());
  }, [dispatch, id, lang, goBackToList]);

  // Role options come from GET /company_roles, which requires Roles Management
  // view permission — not Employees. Skip the call for view-only users and when
  // the session cannot list roles (would 403 and surface "Unauthorized Action").
  useEffect(() => {
    if (!canEdit || !canViewRoles) return;
    dispatch(getCompanyRolesRequest({ page: 1, items: 100 }));
  }, [dispatch, canEdit, canViewRoles]);

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companyTeamsPermissions, label: teamCopy.title },
      { path: EMPLOYEES_TAB_PATH, label: teamCopy.tabs.employees },
      { path: "", label: employee?.name || "" },
    ],
    [employee?.name, teamCopy.tabs.employees, teamCopy.title],
  );

  const exitEdit = useCallback(() => {
    if (editFromListRef.current) {
      goBackToList();
      return;
    }
    setEditing(false);
  }, [goBackToList]);

  /** Reset the editable fields from the loaded employee record. */
  const hydrateForm = () => {
    if (!employee) return;
    setName(employee.name || "");
    setPhone(buildFullInternationalPhone(employee.phoneNumber, employee.dialCode));
    setPhoneCountryDialCode(
      String(employee.dialCode || "966").replace(/\D/g, "") || "966",
    );
    setPhoneCountryIso(employee.countryIso || "sa");
    setRole(resolveRoleOption(employee, roleOptions));
    setPictureFile(null);
    setPicturePreview(employee.image || null);
    setSubmitted(false);
    setTouched({});
  };

  const enterEdit = () => {
    hydrateForm();
    editBaselineRef.current = createEmployeeEditBaseline(employee, roleOptions);
    setEditing(true);
  };

  useEffect(() => {
    if (!editing || !employee) return;
    if (canViewRoles && roleOptions.length === 0) return;
    setRole((current) => {
      if (current) return current;
      const resolved = resolveRoleOption(employee, roleOptions);
      if (resolved && editBaselineRef.current) {
        editBaselineRef.current = {
          ...editBaselineRef.current,
          roleId: String(resolved.id),
        };
      }
      return resolved;
    });
  }, [editing, employee, roleOptions, canViewRoles]);

  const cancelEdit = () => {
    if (updating || uploadState.target) return;
    exitEdit();
  };

  // When arriving from the list's "Edit" action, open directly in edit mode
  // once the employee record has loaded (one time only — Cancel won't re-trigger).
  const autoEnteredEditRef = useRef(false);
  useEffect(() => {
    if (
      employee &&
      canEdit &&
      location.state?.startInEdit &&
      !autoEnteredEditRef.current
    ) {
      autoEnteredEditRef.current = true;
      enterEdit();
    }
    // `enterEdit` reads the latest employee/roles via closure; only re-run when
    // the loaded employee changes.
  }, [employee, location.state, canEdit]);

  const trimmedName = name.trim();

  const errors = useMemo(() => {
    const next = {};
    if (!trimmedName) next.name = formCopy.validations.nameRequired;
    return next;
  }, [trimmedName, formCopy.validations]);

  const isFormValid = useMemo(
    () => Object.keys(errors).length === 0,
    [errors],
  );

  const isDirty = useMemo(
    () =>
      editing &&
      isEmployeeEditDirty(editBaselineRef.current, {
        name,
        phone,
        phoneCountryDialCode,
        role,
        pictureFile,
        picturePreview,
      }),
    [
      editing,
      name,
      phone,
      phoneCountryDialCode,
      role,
      pictureFile,
      picturePreview,
    ],
  );

  const showError = (field) =>
    Boolean((submitted || touched[field]) && errors[field]);

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const uploadingPicture = uploadState.target === "profile_picture";
  const busy = updating || uploadingPicture;

  const openDeleteModal = () => setConfirmingDelete(true);

  const closeDeleteModal = () => {
    if (deleting) return;
    setConfirmingDelete(false);
  };

  const confirmDelete = () => {
    if (!employee || deleting) return;
    dispatch(
      deleteCompanyUserRequest({ id: employee.id, onSuccess: goBackToList }),
    );
  };

  const handleUpdate = async () => {
    setSubmitted(true);
    if (!isFormValid || busy || !isDirty) return;

    let profilePictureUrl;
    if (pictureFile) {
      try {
        profilePictureUrl = await uploadFileWithProgress(
          pictureFile,
          "profile_picture",
          "employee",
          setUploadState,
        );
      } finally {
        setUploadState({ target: null, percent: 0 });
      }
      if (!profilePictureUrl) return;
    }

    const data = buildUpdateEmployeePayload(editBaselineRef.current, {
      name: trimmedName,
      phone,
      phoneCountryDialCode,
      role,
      pictureFile,
      picturePreview,
      profilePictureUrl,
      employeeImage: employee.image,
    });

    if (Object.keys(data).length === 0) return;

    dispatch(
      updateCompanyUserRequest({
        id: employee.id,
        data,
        successMessage: copy.updateSuccess,
        onSuccess: exitEdit,
      }),
    );
  };

  if (!employee) {
    // While loading or on 404 the page renders nothing — toast handles errors.
    return null;
  }

  return (
    <div className="team-management view-employee">
      <div className="mt-2">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
      </div>

      {!editing && (
        <UIPageHeader
          className="company-page-header view-employee__header"
          avatar={{
            image: employee.image || undefined,
            name: employee.name,
            size: "lg",
          }}
          title={employee.name}
          status={employee.role ? "role" : undefined}
          statusLabel={employee.role}
          statusTone="primarySolid"
          statusPreset="header"
          subtitle={
            <span className="view-employee__id" dir="ltr">
              {copy.idLabel} #{employee.id}
            </span>
          }
          actions={
            <>
              {canEdit && (
                <UIButton
                  type="button"
                  appearance="gradient"
                  size="large"
                  startIcon={<Pencil size={16} />}
                  onClick={enterEdit}
                >
                  {copy.editProfile}
                </UIButton>
              )}
              {canDelete && !employee.isCompanyOwner && (
                <UIButton
                  type="button"
                  color="error"
                  size="large"
                  startIcon={<Trash2 size={16} />}
                  onClick={openDeleteModal}
                  disabled={deleting}
                >
                  {copy.deleteEmployee}
                </UIButton>
              )}
            </>
          }
        />
      )}

      <div className="view-employee__card">
        <div className="view-employee__card-header">{copy.detailsTitle}</div>

        {editing ? (
          <div className="add-employee__card-body">
            <div className="add-employee__avatar-col">
              <UIUpload
                name="profilePicture"
                className="add-employee__avatar"
                variant="logo"
                label={formCopy.pictureLabel}
                uploadText={formCopy.uploadPicture}
                changeText={formCopy.changePicture}
                preview={picturePreview || ""}
                file={pictureFile}
                showRemove
                isUploading={uploadingPicture}
                uploadProgress={uploadingPicture ? uploadState.percent : undefined}
                uploadProgressLabel={
                  uploadingPicture
                    ? `${formCopy.uploading} · ${uploadState.percent}%`
                    : null
                }
                onChange={(file, preview) => {
                  if (!file) {
                    setPictureFile(null);
                    setPicturePreview(null);
                    return;
                  }
                  setPictureFile(file);
                  setPicturePreview(
                    typeof preview === "string" ? preview : null,
                  );
                }}
                disabled={busy}
              />
            </div>

            <div className="add-employee__grid">
              <UITextField
                label={copy.fields.name}
                name="name"
                required
                value={name}
                placeholder={formCopy.fields.namePlaceholder}
                onChange={(event) => setName(event.target.value)}
                onBlur={() => markTouched("name")}
                error={showError("name")}
                helperText={showError("name") ? errors.name : null}
              />
              {/* Email is not editable. */}
              <UITextField
                label={copy.fields.email}
                name="email"
                required
                value={employee.email}
                disabled
              />
              <UIPhoneField
                label={copy.fields.mobileNumber}
                name="mobileNumber"
                country={phoneCountryIso}
                preferredCountries={["eg", "sa", "ae"]}
                value={phone}
                formValueIsFullInternational
                disabled={busy}
                onChange={(fullValue, countryData) => {
                  setPhone(fullValue);
                  const dc = countryData?.dialCode;
                  if (dc != null && dc !== "") {
                    setPhoneCountryDialCode(String(dc).replace(/\D/g, ""));
                  }
                }}
              />
              {canViewRoles ? (
              <UISelect
                label={copy.fields.role}
                name="role"
                placeholder={formCopy.fields.rolePlaceholder}
                options={roleOptions}
                value={role}
                disabled={busy}
                isOptionEqualToValue={(option, selected) =>
                  String(option?.id) === String(selected?.id)
                }
                onChange={(nextValue) => setRole(nextValue)}
              />
            ) : (
              <UITextField
                label={copy.fields.role}
                name="role"
                value={employee.role || copy.emptyValue}
                disabled
              />
            )}
            </div>
          </div>
        ) : (
          <div className="view-employee__grid">
            <DetailField
              label={copy.fields.name}
              value={employee.name}
              emptyValue={copy.emptyValue}
            />
            <DetailField
              label={copy.fields.email}
              value={employee.email}
              emptyValue={copy.emptyValue}
            />
            <DetailField
              label={copy.fields.mobileNumber}
              value={employee.mobileNumber}
              emptyValue={copy.emptyValue}
              ltr
            />
            <DetailField
              label={copy.fields.role}
              value={employee.role}
              emptyValue={copy.emptyValue}
            />
          </div>
        )}
      </div>

      {editing && (
        <UIPageFormFooter
          cancelLabel={copy.cancel}
          submitLabel={copy.update}
          onCancel={cancelEdit}
          onSubmit={handleUpdate}
          cancelDisabled={busy}
          submitDisabled={busy || !isDirty || !isFormValid}
          submitLoading={busy}
        />
      )}

      <DeleteEmployeeModal
        open={confirmingDelete}
        employee={employee}
        copy={teamCopy.employees.deleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default ViewEmployee;
